import { type HTMLAttributes, type ReactNode } from "react"

type DateDisplayProps = Omit<HTMLAttributes<HTMLTimeElement>, "children"> & {
  children?: string | Date | null
  fallback?: ReactNode
}

type TimeDisplayProps = DateDisplayProps & {
  includeSeconds?: boolean
}

type DayDisplayProps = DateDisplayProps & {
  locale?: string
  long?: boolean
}

type WeekDisplayProps = DateDisplayProps & {
  locale?: string
}

function parseDate(value?: string | Date | null): Date | null {
  if (!value) return null
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return null
  return date
}

function pad2(value: number): string {
  return String(value).padStart(2, "0")
}

function formatLocalDate(date: Date): string {
  const year = date.getFullYear()
  const month = pad2(date.getMonth() + 1)
  const day = pad2(date.getDate())
  return `${year}-${month}-${day}`
}

function formatLocalTime(date: Date, includeSeconds = false): string {
  const hours = pad2(date.getHours())
  const minutes = pad2(date.getMinutes())
  if (!includeSeconds) return `${hours}:${minutes}`
  const seconds = pad2(date.getSeconds())
  return `${hours}:${minutes}:${seconds}`
}

function getISOWeek(date: Date): number {
  const local = new Date(date)
  local.setHours(0, 0, 0, 0)

  const day = local.getDay() || 7
  local.setDate(local.getDate() + 4 - day)

  const yearStart = new Date(local.getFullYear(), 0, 1)
  return Math.ceil((((local.getTime() - yearStart.getTime()) / 86400000) + 1) / 7)
}

function getWeekPrefix(locale?: string): "w" | "cw" {
  const normalized = (locale || Intl.DateTimeFormat().resolvedOptions().locale).toLowerCase()
  return normalized.startsWith("de") ? "cw" : "w"
}

export function formatWeekLabel(value: string | Date, locale?: string): string {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  const week = pad2(getISOWeek(date))
  const prefix = getWeekPrefix(locale)
  return `${prefix}${week}`
}

export function formatDayLabel(value: string | Date, locale?: string, long = false): string {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return "-"
  return new Intl.DateTimeFormat(locale, {
    weekday: long ? "long" : "short",
  }).format(date)
}

export function DateText({ children, fallback = "-", ...props }: DateDisplayProps) {
  const date = parseDate(children)
  if (!date) return <>{fallback}</>

  const dateString = formatLocalDate(date)
  return (
    <time dateTime={date.toISOString()} {...props}>
      {dateString}
    </time>
  )
}

export function TimeText({ children, fallback = "-", includeSeconds = false, ...props }: TimeDisplayProps) {
  const date = parseDate(children)
  if (!date) return <>{fallback}</>

  const timeString = formatLocalTime(date, includeSeconds)
  return (
    <time dateTime={date.toISOString()} {...props}>
      {timeString}
    </time>
  )
}

export function DateTimeText({ children, fallback = "-", includeSeconds = false, ...props }: TimeDisplayProps) {
  const date = parseDate(children)
  if (!date) return <>{fallback}</>

  const dateTimeString = `${formatLocalDate(date)} ${formatLocalTime(date, includeSeconds)}`
  return (
    <time dateTime={date.toISOString()} {...props}>
      {dateTimeString}
    </time>
  )
}

export function WeekText({ children, fallback = "-", locale, ...props }: WeekDisplayProps) {
  const date = parseDate(children)
  if (!date) return <>{fallback}</>

  return (
    <time dateTime={date.toISOString()} {...props}>
      {formatWeekLabel(date, locale)}
    </time>
  )
}

export function DayText({ children, fallback = "-", locale, long = false, ...props }: DayDisplayProps) {
  const date = parseDate(children)
  if (!date) return <>{fallback}</>

  return (
    <time dateTime={date.toISOString()} {...props}>
      {formatDayLabel(date, locale, long)}
    </time>
  )
}
